import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
} from "@mui/material";
import { Upload } from "@mui/icons-material";

/**
 * Shared upload widget used by both document-management panels.
 *
 * The "primary" target is implicit (whichever panel hosts this widget). An
 * optional `secondaryTarget` renders a single checkbox inside the dropzone
 * so the same file can also be sent to the other pool. The caller decides
 * the wording — keep it plain (e.g. "Also add to Clinical References").
 */
const DocumentUploadZone = ({
  disabled = false,
  hasPromptId = true,
  acceptedExtensions = [],
  secondaryTarget = null,
  onFilesAccepted,
  inputId = "document-upload",
  trailing = null,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [includeSecondary, setIncludeSecondary] = useState(false);

  // Reset the secondary toggle if the caller swaps in a different target.
  useEffect(() => {
    setIncludeSecondary(false);
  }, [secondaryTarget?.value]);

  const inputDisabled = disabled || !hasPromptId;

  const handleFiles = (files) => {
    if (!files?.length || inputDisabled) return;
    onFilesAccepted?.(files, includeSecondary);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleFiles(Array.from(event.dataTransfer.files || []));
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: "center",
          borderStyle: "dashed",
          borderColor: isDragging ? "primary.main" : "divider",
          bgcolor: isDragging ? "action.hover" : "background.paper",
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!inputDisabled) setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
      >
        <Upload sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
        <Typography variant="subtitle1">Upload Documents</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Drag files here or choose them manually.
        </Typography>
        {acceptedExtensions.length ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: "block" }}
          >
            Accepted: {acceptedExtensions.join(", ")}
          </Typography>
        ) : null}
        <input
          id={inputId}
          type="file"
          multiple
          accept={acceptedExtensions.join(",")}
          style={{ display: "none" }}
          onChange={(event) => {
            handleFiles(Array.from(event.target.files || []));
            event.target.value = "";
          }}
          disabled={inputDisabled}
        />
        <label htmlFor={inputId}>
          <Button
            component="span"
            variant="outlined"
            startIcon={<Upload />}
            disabled={inputDisabled}
          >
            Select Files
          </Button>
        </label>
      </Paper>

      {secondaryTarget || trailing ? (
        <Box
          sx={{
            mt: 0.5,
            pl: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {secondaryTarget ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeSecondary}
                  onChange={(event) => setIncludeSecondary(event.target.checked)}
                  disabled={disabled}
                />
              }
              label={secondaryTarget.label}
            />
          ) : <span />}
          {trailing}
        </Box>
      ) : null}
    </Box>
  );
};

export default DocumentUploadZone;
